import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Paper, Divider, CircularProgress,
  Chip, useTheme, alpha
} from '@mui/material';
import {
  Description, CheckCircle, People, Inventory,
  HourglassEmpty, Cancel, TrendingUp, AttachMoney,
  ArrowUpward, ArrowDownward
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

// ─── Types ────────────────────────────────────────────────────────────────────
interface Stats {
  totalOffers: number; draftOffers: number; sentOffers: number;
  acceptedOffers: number; rejectedOffers: number;
  totalCustomers: number; totalProducts: number; totalRevenue: number;
}
interface LmePoint { date: string; lme: number; spot: number; }

// ─── StatCard ─────────────────────────────────────────────────────────────────
const StatCard: React.FC<{
  label: string; value: number | string; color: string;
  icon: React.ReactNode; subtitle?: string;
}> = ({ label, value, color, icon, subtitle }) => {
  const theme = useTheme();
  return (
      <Paper sx={{
        p: 2.5, flex: 1, minWidth: 140,
        border: `1px solid ${theme.palette.divider}`,
        borderTop: `3px solid ${color}`,
        bgcolor: theme.palette.background.paper,
        transition: 'box-shadow .15s',
        '&:hover': { boxShadow: theme.shadows[4] },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{
              fontSize: '11px', fontWeight: 600, letterSpacing: '0.05em',
              textTransform: 'uppercase', color: theme.palette.text.secondary, mb: 0.75,
            }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1, mb: 0.4 }}>
              {value}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                  {subtitle}
                </Typography>
            )}
          </Box>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            bgcolor: alpha(color, 0.12),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color,
          }}>
            {icon}
          </Box>
        </Box>
      </Paper>
  );
};

// ─── LME Widget ───────────────────────────────────────────────────────────────
const LmeWidget: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [history, setHistory] = useState<LmePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const chartRef      = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    api.get('/lme/history')
        .then(r => setHistory(r.data))
        .catch(console.error)
        .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!history.length || !chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const gridColor  = isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.05)';
    const tickColor  = isDark ? '#8b949e' : '#8a92a6';
    const lineColor  = '#1D9E75';
    const fillColor  = isDark ? 'rgba(29,158,117,.12)' : 'rgba(29,158,117,.08)';

    chartInstance.current = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels: history.map(h => {
          const d = new Date(h.date);
          return `${d.getDate()}/${d.getMonth() + 1}`;
        }),
        datasets: [{
          label: 'LME Al',
          data: history.map(h => h.lme),
          borderColor: lineColor, borderWidth: 2,
          pointRadius: history.length > 30 ? 0 : 3,
          pointBackgroundColor: lineColor,
          tension: 0.35, fill: true,
          backgroundColor: fillColor,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: isDark ? '#1e2d3d' : '#fff',
            titleColor: isDark ? '#e6edf3' : '#1a2535',
            bodyColor:  isDark ? '#8b949e'  : '#8a92a6',
            borderColor: isDark ? 'rgba(255,255,255,.1)' : '#e8eaf0',
            borderWidth: 1,
            callbacks: { label: (ctx: any) => `$${ctx.parsed.y.toLocaleString()} /ton` },
          },
        },
        scales: {
          x: {
            grid: { color: gridColor },
            ticks: { color: tickColor, font: { size: 10 }, maxTicksLimit: 10 },
          },
          y: {
            grid: { color: gridColor },
            ticks: {
              color: tickColor, font: { size: 10 },
              callback: (v: number | string) => '$' + Number(v).toLocaleString(),
            },
          },
        },
      },
    });
  }, [history, isDark]);

  if (loading) return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
  );
  if (!history.length) return null;

  const current = history[history.length - 1]?.lme || 0;
  const prev7   = history.length >= 8  ? history[history.length - 8]?.lme  : history[0]?.lme;
  const prev30  = history.length >= 31 ? history[history.length - 31]?.lme : history[0]?.lme;
  const change7  = current - (prev7  || current);
  const change30 = current - (prev30 || current);
  const ma7   = history.slice(-7).reduce((s, h) => s + h.lme, 0)  / Math.min(7,  history.length);
  const ma30  = history.slice(-30).reduce((s, h) => s + h.lme, 0) / Math.min(30, history.length);
  const min90 = Math.min(...history.map(h => h.lme));
  const max90 = Math.max(...history.map(h => h.lme));
  const pct   = max90 > min90 ? Math.round((current - min90) / (max90 - min90) * 100) : 50;
  const signal = pct < 35 ? 'buy' : pct > 65 ? 'sell' : 'wait';
  const signalMap = {
    buy:  { label: 'Al Fırsatı', color: '#16A34A', bg: isDark ? '#16A34A22' : '#DCFCE7' },
    wait: { label: 'Bekle',      color: '#B45309', bg: isDark ? '#B4530922' : '#FEF3C7' },
    sell: { label: 'Dikkatli',   color: '#DC2626', bg: isDark ? '#DC262622' : '#FEE2E2' },
  };
  const sig = signalMap[signal];

  const metrics = [
    { label: 'Güncel LME',    val: `$${current.toLocaleString()}`, sub: '/ton', color: '#16A34A' },
    { label: '7G Değişim',    val: `${change7  >= 0 ? '+' : ''}${Math.round(change7)}`,  sub: '$/ton', color: change7  >= 0 ? '#16A34A' : '#DC2626' },
    { label: '30G Değişim',   val: `${change30 >= 0 ? '+' : ''}${Math.round(change30)}`, sub: '$/ton', color: change30 >= 0 ? '#16A34A' : '#DC2626' },
    { label: 'MA7 / MA30',    val: `$${Math.round(ma7).toLocaleString()}`, sub: `/ $${Math.round(ma30).toLocaleString()}`, color: 'primary' },
    { label: 'Aralık Konumu', val: `%${pct}`, sub: `${min90.toLocaleString()}–${max90.toLocaleString()}`, color: 'primary' },
  ];

  return (
      <Paper sx={{
        p: 3, border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '8px',
              bgcolor: alpha('#16A34A', isDark ? 0.2 : 0.1),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TrendingUp sx={{ color: '#16A34A', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.2 }}>
                LME Alüminyum
              </Typography>
              <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                metals.dev · otomatik güncellenir
              </Typography>
            </Box>
          </Box>
          <Chip label={sig.label} size="small" sx={{
            bgcolor: sig.bg, color: sig.color, fontWeight: 700, fontSize: '11px',
          }} />
        </Box>

        {/* Metrikler */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
          {metrics.map(m => (
              <Box key={m.label} sx={{
                p: 1.5, flex: '1 1 100px', minWidth: 100,
                bgcolor: alpha(theme.palette.text.primary, isDark ? 0.04 : 0.03),
                borderRadius: '8px',
              }}>
                <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary, mb: 0.4, lineHeight: 1.3 }}>
                  {m.label}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', color: m.color === 'primary' ? theme.palette.text.primary : m.color, lineHeight: 1.3 }}>
                  {m.val}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary }}>
                  {m.sub}
                </Typography>
              </Box>
          ))}
        </Box>

        {/* Grafik */}
        <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
          <canvas ref={chartRef} />
        </Box>
      </Paper>
  );
};

// ─── Bekleyen Teklif Kartı ────────────────────────────────────────────────────
const PendingCard: React.FC<{
  count: number; label: string; sublabel: string;
  color: string; icon: React.ReactNode; onClick: () => void;
}> = ({ count, label, sublabel, color, icon, onClick }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
      <Paper onClick={onClick} sx={{
        flex: 1, minWidth: 200, p: 2,
        border: `1.5px solid ${color}`,
        bgcolor: alpha(color, isDark ? 0.1 : 0.04),
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2,
        transition: 'all .15s',
        '&:hover': { bgcolor: alpha(color, isDark ? 0.16 : 0.08), transform: 'translateY(-1px)' },
      }}>
        <Box sx={{
          width: 40, height: 40, bgcolor: color, borderRadius: '8px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '13px', color }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
            {sublabel}
          </Typography>
        </Box>
      </Paper>
  );
};

// ─── DashboardPage ────────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats]   = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/dashboard/stats${isAdmin ? '?admin=true' : ''}`)
        .then(r => setStats(r.data))
        .catch(console.error)
        .finally(() => setLoading(false));
  }, [isAdmin]);

  if (loading) return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
  );

  const hasPending = (stats?.draftOffers || 0) > 0 || (stats?.sentOffers || 0) > 0;

  return (
      <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

        {/* ── Karşılama ── */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
            Hoş geldiniz, {user?.fullName} 👋
          </Typography>
          <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, mt: 0.5 }}>
            {isAdmin ? 'Tüm sistem istatistikleri' : 'Kendi teklif istatistikleriniz'}
          </Typography>
        </Box>

        {/* ── Gelir kartı ── */}
        <Paper sx={{
          p: 3, mb: 3,
          background: isDark
              ? 'linear-gradient(135deg, #1e2d45 0%, #0f1a2e 100%)'
              : 'linear-gradient(135deg, #1A3A6B 0%, #0f2652 100%)',
          color: '#fff', position: 'relative', overflow: 'hidden',
          border: 'none',
        }}>
          {/* Dekoratif daireler */}
          <Box sx={{ position: 'absolute', right: -24, top: -24, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.05)' }} />
          <Box sx={{ position: 'absolute', right: 24, bottom: -32, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(192,57,43,.2)' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,.55)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}>
                Kabul Edilen Gelir
              </Typography>
              <Typography sx={{ fontSize: 36, fontWeight: 700, lineHeight: 1, mb: 0.5 }}>
                {(stats?.totalRevenue || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,.45)', fontSize: '13px' }}>USD</Typography>
            </Box>
            <Box sx={{
              width: 52, height: 52, bgcolor: 'rgba(255,255,255,.1)',
              borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AttachMoney sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
          </Box>
        </Paper>

        {/* ── Teklif istatistikleri ── */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <StatCard label="Toplam Teklif"  value={stats?.totalOffers    || 0} color="#1A3A6B" icon={<Description />} />
          <StatCard label="Kabul Edilen"   value={stats?.acceptedOffers || 0} color="#16A34A" icon={<CheckCircle />}
                    subtitle={stats?.totalRevenue ? `${stats.totalRevenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} USD` : undefined} />
          <StatCard label="Bekleyen"
                    value={(stats?.draftOffers || 0) + (stats?.sentOffers || 0)}
                    color="#B45309" icon={<HourglassEmpty />}
                    subtitle={`${stats?.draftOffers || 0} taslak · ${stats?.sentOffers || 0} gönderildi`} />
          <StatCard label="Reddedilen"     value={stats?.rejectedOffers || 0} color="#DC2626" icon={<Cancel />} />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* ── Müşteri & Ürün ── */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <StatCard label="Müşteri" value={stats?.totalCustomers || 0} color="#0E7490" icon={<People />} />
          <StatCard label="Ürün"    value={stats?.totalProducts  || 0} color="#7C3AED" icon={<Inventory />} />
          <Box sx={{ flex: 2, minWidth: 160 }} />
        </Box>

        {/* ── Bekleyen uyarılar ── */}
        {hasPending && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                {(stats?.draftOffers || 0) > 0 && (
                    <PendingCard
                        count={stats!.draftOffers}
                        label={`${stats!.draftOffers} teklif taslakta bekliyor`}
                        sublabel="Göndermek için tıklayın →"
                        color="#F59E0B"
                        icon={<HourglassEmpty sx={{ color: '#fff', fontSize: 20 }} />}
                        onClick={() => navigate('/offers?status=DRAFT')}
                    />
                )}
                {(stats?.sentOffers || 0) > 0 && (
                    <PendingCard
                        count={stats!.sentOffers}
                        label={`${stats!.sentOffers} teklif yanıt bekliyor`}
                        sublabel="Takip etmek için tıklayın →"
                        color="#3B82F6"
                        icon={<Description sx={{ color: '#fff', fontSize: 20 }} />}
                        onClick={() => navigate('/offers?status=SENT')}
                    />
                )}
              </Box>
            </>
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* ── LME Widget ── */}
        <LmeWidget />
      </Box>
  );
};

export default DashboardPage;
